// Country code <-> name conversion using the runtime's built-in Intl.DisplayNames,
// so we never hand-maintain a country list. Replaces the legacy `countryLookup`
// map in importCSVs.gs.
//
// - Shopify CSVs give an ISO-2 code (e.g. "GB") -> expand to a name.
// - Etsy/eBay CSVs give a full name -> derive the ISO-2 code (for `countryCode`
//   and EU/IOSS detection).

const regionNames =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

/** ISO-2 region code -> English country name (e.g. "GB" -> "United Kingdom"). */
export function codeToCountry(code) {
  if (!code) return '';
  const c = String(code).trim().toUpperCase();
  if (c.length !== 2 || !regionNames) return code; // already a name / unsupported runtime
  try {
    const name = regionNames.of(c);
    return name && name !== c ? name : code;
  } catch {
    return code;
  }
}

// Lazily-built reverse map: lowercased country name -> ISO-2 code.
// We iterate every 2-letter combination and keep the ones Intl recognises.
let _reverse = null;
function reverseMap() {
  if (_reverse) return _reverse;
  _reverse = new Map();
  if (!regionNames) return _reverse;
  const A = 'A'.charCodeAt(0);
  for (let i = 0; i < 26; i++) {
    for (let j = 0; j < 26; j++) {
      const code = String.fromCharCode(A + i) + String.fromCharCode(A + j);
      let name;
      try {
        name = regionNames.of(code);
      } catch {
        continue;
      }
      if (name && name !== code) _reverse.set(name.toLowerCase(), code);
    }
  }
  return _reverse;
}

function normaliseName(name) {
  // Lowercase, trim, and drop a leading "the " (e.g. "The Netherlands").
  return String(name).trim().toLowerCase().replace(/^the\s+/, '');
}

// The UK's dual code (GB/UK) and its constituent countries trip up the generic
// reverse lookup (Intl resolves both "GB" and "UK" to "United Kingdom", and the
// later one wins), so map them explicitly to the canonical ISO-2 code "GB".
const UK_ALIASES = {
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
};

/** Country name (or ISO-2 code) -> ISO-2 code, or '' if not derivable. */
export function countryToCode(country) {
  if (!country) return '';
  const raw = String(country).trim();
  const alias = UK_ALIASES[normaliseName(raw)];
  if (alias) return alias;
  if (raw.length === 2) return raw.toUpperCase();
  return reverseMap().get(normaliseName(raw)) || '';
}

// EU member states by ISO-2 code (drives the IOSS flag). Codes only — no names.
export const EU_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
]);

/** True if the given country (name or ISO-2 code) is an EU member. */
export function isEUCountry(country) {
  const code = countryToCode(country);
  return code ? EU_CODES.has(code) : false;
}
